import { useCallback, useMemo } from 'react';
import { useSendTransaction } from '@privy-io/react-auth/solana';
import { 
  Connection, 
  Transaction,
  SystemProgram,
  PublicKey,
  LAMPORTS_PER_SOL,
} from '@solana/web3.js';
import { solanaUtils } from '../utils/solanaUtils';
import { waitForTransactionConfirmation } from '../utils/transactionParser';

export interface TransactionResult {
  success: boolean;
  signature?: string;
  error?: string;
}

export const useTransactions = (solanaWallet: any) => {
  const { sendTransaction } = useSendTransaction();
  
  // Check wallet type
  const isEmbeddedWallet = solanaWallet?.walletClientType === 'privy';
  const isExternalWallet = solanaWallet?.walletClientType === 'phantom' || 
                           solanaWallet?.connectorType === 'solana_adapter';
  
  const connection = useMemo(() => new Connection(solanaUtils.getRpcEndpoint(), 'confirmed'), []);

  /**
   * Withdraw SOL to another address
   */
  const withdrawSol = useCallback(async (toAddress: string, amountSol: number): Promise<TransactionResult> => {
    if (!solanaWallet?.address) {
      return { success: false, error: 'Wallet not connected' };
    }

    try {
      // Create withdrawal transaction
      const transaction = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: new PublicKey(solanaWallet.address),
          toPubkey: new PublicKey(toAddress),
          lamports: Math.floor(amountSol * LAMPORTS_PER_SOL)
        })
      );

      // Set recent blockhash and fee payer
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = new PublicKey(solanaWallet.address);

      // Simulate transaction before signing
      try {
        transaction.signatures = transaction.signatures.map(sig => ({
          ...sig,
          signature: null
        }));
        
        const simulation = await connection.simulateTransaction(transaction);
        
        if (simulation.value.err) {
          console.error('Transaction simulation failed:', simulation.value.err, simulation.value.logs);
          throw new Error(`Transaction will fail: ${JSON.stringify(simulation.value.err)}`);
        }
      } catch (simError: any) {
        throw new Error(`Pre-flight simulation failed: ${simError.message}`);
      }

      // Sign and send transaction
      let signature: string;

      if (isExternalWallet && solanaWallet?.signTransaction) {
        // External wallets (Phantom) - use separate sign/send pattern
        try {
          const signedTx = await solanaWallet.signTransaction(transaction);
          signature = await connection.sendRawTransaction(signedTx.serialize(), {
            skipPreflight: true,  // Already simulated above
            preflightCommitment: 'confirmed',
            maxRetries: 3
          });
        } catch (walletError: any) {
          console.error('Wallet signing failed:', walletError);
          throw new Error(`Wallet signing failed: ${walletError.message || 'Unknown error'}`);
        }
      } else if (isEmbeddedWallet) {
        // Embedded wallets (Privy)
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: true },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      } else {
        // Fallback
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: false },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      }

      // Wait for confirmation
      const confirmed = await waitForTransactionConfirmation(connection, signature);
      if (!confirmed) {
        throw new Error('Transaction failed to confirm');
      }

      return {
        success: true,
        signature
      };

    } catch (error: any) {
      console.error('❌ SOL withdrawal failed:', error);
      return {
        success: false,
        error: error.message || 'Failed to withdraw SOL'
      };
    }
  }, [solanaWallet, sendTransaction, connection, isEmbeddedWallet, isExternalWallet]);

  return {
    withdrawSol
  };
};
